import { useMemo, useState } from 'react'
import { Box, Button, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  type GridColDef,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid'
import MetricStateIndicator from '../common/MetricStateIndicator'
import { DEFAULT_SCORE_PALETTE } from '../common/ScoreBadge'
import { MetricState } from '../common'
import PortfolioInfoPopup from './PortfolioInfoPopup'
import PropertyAalMetadata from './PropertyAalMetadata'
import { combinedPropertyAal, summarizeCombinedAal, type PortfolioHazardAal } from './portfolioAal'
import {
  insuredPropertyValue,
  parseFinancialValue,
  supportedHorizonLabel,
  type PortfolioHazardRisk,
  type PortfolioRiskProperty,
} from './portfolioRisk'

type Props = {
  properties: PortfolioRiskProperty[]
  risks: PortfolioHazardRisk[]
  hazardAals: PortfolioHazardAal[]
  view: 'risk' | 'aal'
  loading: boolean
  currencyISO: string
  onOpenProperty: (id: number) => void
  onOpenHazard: (id: number, hazardCode: string) => void
}

function RiskTableToolbar() {
  return (
    <GridToolbarContainer sx={{ px: 0, pb: 2, gap: 1, justifyContent: 'space-between' }}>
      <GridToolbarQuickFilter placeholder="Search properties…" debounceMs={200} />
      <Stack direction="row" spacing={1}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
      </Stack>
    </GridToolbarContainer>
  )
}

export default function PortfolioRiskTable({
  properties,
  risks,
  hazardAals,
  view,
  loading,
  currencyISO,
  onOpenProperty,
  onOpenHazard,
}: Props) {
  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>({
    type: false,
    country: false,
    structure: false,
    contents: false,
  })
  const money = useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyISO,
        notation: 'compact',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [currencyISO],
  )
  const exactMoney = useMemo(
    () =>
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyISO,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [currencyISO],
  )
  const aalSummary = summarizeCombinedAal(
    hazardAals,
    properties.map(property => property.id),
  )

  const columns = useMemo<GridColDef<PortfolioRiskProperty>[]>(() => {
    const financialColumn = (
      field: string,
      headerName: string,
      getter: (property: PortfolioRiskProperty) => number | null,
    ): GridColDef<PortfolioRiskProperty> => ({
      field,
      headerName,
      width: 145,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueGetter: params => getter(params.row),
      renderCell: params =>
        params.value == null ? (
          '—'
        ) : (
          <Tooltip title={exactMoney.format(params.value)}>
            <span>{money.format(params.value)}</span>
          </Tooltip>
        ),
    })
    return [
      {
        field: 'name',
        headerName: 'Property',
        width: 250,
        valueGetter: params => params.row.name || `Property ${params.row.id}`,
        renderCell: params => (
          <Button
            variant="text"
            size="small"
            onClick={() => onOpenProperty(params.row.id)}
            sx={{
              p: 0,
              textTransform: 'none',
              textAlign: 'left',
              justifyContent: 'flex-start',
              minWidth: 0,
              width: '100%',
            }}
          >
            <Stack minWidth={0}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {params.value}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[params.row.address?.town, params.row.address?.postcode].filter(Boolean).join(', ')}
              </Typography>
            </Stack>
          </Button>
        ),
      },
      { field: 'type', headerName: 'Property type', width: 160, valueGetter: params => params.row.type?.label ?? null },
      {
        field: 'country',
        headerName: 'Country',
        width: 120,
        valueGetter: params => params.row.address?.country ?? null,
      },
      financialColumn('total_value', 'Total Value', insuredPropertyValue),
      financialColumn('revenue', 'Annual revenue', property => parseFinancialValue(property.insurance?.revenue)),
      financialColumn('structure', 'Structure value', property => parseFinancialValue(property.insurance?.structure)),
      financialColumn('contents', 'Contents value', property => parseFinancialValue(property.insurance?.contents)),
      ...(view === 'aal'
        ? [
            {
              ...financialColumn('combined_aal', 'Combined AAL / year', property =>
                combinedPropertyAal(hazardAals, property.id),
              ),
              width: 195,
              renderCell: params => {
                if (aalSummary.loading) return <Skeleton variant="rounded" width={100} height={24} />
                if (aalSummary.error)
                  return (
                    <Button
                      size="small"
                      onClick={() => hazardAals.filter(hazard => hazard.error).forEach(hazard => hazard.retry?.())}
                    >
                      Reload
                    </Button>
                  )
                const propertyName = params.row.name || `Property ${params.row.id}`
                return (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip
                      title={
                        params.value == null
                          ? 'No annual-loss result for this selection'
                          : `${exactMoney.format(params.value)} per year`
                      }
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {params.value == null ? '—' : money.format(params.value)}
                      </Typography>
                    </Tooltip>
                    {params.value != null && (
                      <PortfolioInfoPopup
                        title="Combined AAL"
                        informationLabel={`Combined AAL for ${propertyName} information`}
                      >
                        <PropertyAalMetadata
                          hazards={hazardAals}
                          propertyId={params.row.id}
                          propertyName={propertyName}
                          currency={currencyISO}
                        />
                      </PortfolioInfoPopup>
                    )}
                  </Stack>
                )
              },
            } as GridColDef<PortfolioRiskProperty>,
            ...hazardAals
              .filter(hazard => hazard.modelled)
              .map(
                (hazard): GridColDef<PortfolioRiskProperty> => ({
                  ...financialColumn(`aal_${hazard.hazard.name}`, `${hazard.hazard.display_name} AAL`, property =>
                    hazard.periodAvailable && !hazard.loading && !hazard.error
                      ? hazard.byProperty[property.id]?.combined ?? null
                      : null,
                  ),
                  width: 175,
                  renderCell: params => {
                    if (!hazard.periodAvailable)
                      return (
                        <Typography variant="caption" color="text.secondary">
                          {supportedHorizonLabel(hazard.hazard.name)} only
                        </Typography>
                      )
                    if (hazard.loading) return <Skeleton variant="rounded" width={100} height={24} />
                    if (hazard.error)
                      return (
                        <Button size="small" onClick={hazard.retry}>
                          Reload
                        </Button>
                      )
                    if (params.value == null)
                      return (
                        <Tooltip title="No annual-loss result for this selection">
                          <span>—</span>
                        </Tooltip>
                      )
                    const value = hazard.byProperty[params.row.id]
                    return (
                      <Tooltip
                        title={`Damage: ${value.damage == null ? '—' : exactMoney.format(value.damage)} · BI: ${
                          value.bi == null ? '—' : exactMoney.format(value.bi)
                        } per year`}
                      >
                        <Button
                          size="small"
                          aria-label={`View ${hazard.hazard.display_name} annual losses for ${
                            params.row.name || `Property ${params.row.id}`
                          }`}
                          onClick={() => onOpenHazard(params.row.id, hazard.hazard.name)}
                          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                        >
                          {money.format(params.value)}
                        </Button>
                      </Tooltip>
                    )
                  },
                }),
              ),
          ]
        : risks.map(
            (risk): GridColDef<PortfolioRiskProperty> => ({
              field: `risk_${risk.hazard.name}`,
              headerName: risk.hazard.display_name,
              width: 170,
              type: 'number',
              headerAlign: 'left',
              align: 'left',
              valueGetter: params => risk.byProperty[params.row.id]?.score ?? null,
              renderCell: params => {
                if (!risk.config)
                  return (
                    <Typography variant="caption" color="text.secondary">
                      Not configured
                    </Typography>
                  )
                if (!risk.periodAvailable)
                  return (
                    <Typography variant="caption" color="text.secondary">
                      {supportedHorizonLabel(risk.hazard.name)} only
                    </Typography>
                  )
                if (risk.loading) return <Skeleton variant="rounded" width={100} height={24} />
                if (risk.error)
                  return (
                    <MetricStateIndicator
                      state={MetricState.FAILED}
                      variant="chip"
                      message="Unable to load these results."
                    />
                  )
                const value = risk.byProperty[params.row.id]
                if (value?.score == null)
                  return <MetricStateIndicator state={value?.state ?? MetricState.NO_DATA} variant="chip" />
                const colors = DEFAULT_SCORE_PALETTE[value.score] as { background: string; color: string }
                return (
                  <Tooltip title={`View ${risk.hazard.display_name} for this property`}>
                    <Button
                      size="small"
                      aria-label={`View ${risk.hazard.display_name} risk for ${
                        params.row.name || `Property ${params.row.id}`
                      }`}
                      onClick={() => onOpenHazard(params.row.id, risk.hazard.name)}
                      sx={{
                        bgcolor: colors.background,
                        color: colors.color,
                        borderRadius: 2,
                        px: 1.25,
                        py: 0.6,
                        textTransform: 'none',
                        fontSize: 11,
                        fontWeight: 700,
                        '&:hover': { bgcolor: colors.background, filter: 'brightness(0.95)' },
                      }}
                    >
                      {value.score} · {risk.config.labels[value.score]}
                    </Button>
                  </Tooltip>
                )
              },
            }),
          )),
    ]
  }, [
    risks,
    hazardAals,
    view,
    aalSummary.loading,
    aalSummary.error,
    currencyISO,
    money,
    exactMoney,
    onOpenProperty,
    onOpenHazard,
  ])

  return (
    <Box sx={{ height: 600, width: '100%', minWidth: 0 }}>
      <DataGrid
        aria-label={view === 'risk' ? 'Portfolio hazard risk table' : 'Portfolio annual losses table'}
        rows={properties}
        columns={columns}
        loading={loading}
        rowHeight={58}
        disableSelectionOnClick
        columnVisibilityModel={visibility}
        onColumnVisibilityModelChange={setVisibility}
        initialState={{ sorting: { sortModel: [{ field: 'name', sort: 'asc' }] } }}
        components={{ Toolbar: RiskTableToolbar }}
        sx={{ border: 0, '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 } }}
      />
    </Box>
  )
}
